import React, { useState } from 'react';
import { BIBLE_TRANSLATIONS } from '../data/bibleData';

function Settings({ settings, updateSettings, onClose }) {
  const [localSettings, setLocalSettings] = useState(settings);
  const [activeTab, setActiveTab] = useState('general');

  const handleChange = (key, value) => {
    setLocalSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    updateSettings(localSettings);
    onClose();
  };

  const themes = [
    { id: 'default', name: 'Default', color: '#667eea', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', active: true },
    { id: 'dark', name: 'Dark', color: '#2c3e50', gradient: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', active: true },
    { id: 'light', name: 'Light', color: '#f8fafc', gradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', active: true },
    { id: 'nature', name: 'Nature', color: '#27ae60', gradient: 'linear-gradient(135deg, #f0f9f4 0%, #ffffff 100%)', active: true },
    { id: 'sunset', name: 'Sunset', color: '#e74c3c', gradient: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 100%)', active: true },
    { id: 'ocean', name: 'Ocean', color: '#3498db', gradient: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)', active: true },
    { id: 'forest', name: 'Forest', color: '#16a085', gradient: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)', active: true },
    { id: 'purple', name: 'Purple', color: '#9b59b6', gradient: 'linear-gradient(135deg, #faf5ff 0%, #ffffff 100%)', active: true }
  ];

  const fontSizes = [
    { id: 'small', name: 'Small', size: '12px' },
    { id: 'medium', name: 'Medium', size: '14px' },
    { id: 'large', name: 'Large', size: '16px' },
    { id: 'xlarge', name: 'Extra Large', size: '18px' }
  ];

  const tabs = [
    { id: 'general', label: 'General', icon: '⚙️' },
    { id: 'appearance', label: 'Appearance', icon: '🎨' },
    { id: 'bible', label: 'Bible', icon: '📖' },
    { id: 'export', label: 'Export', icon: '📤' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="settings-content">
            <div className="settings-group">
              <h3 className="settings-group-title">Auto-Save</h3>
              <div className="settings-item">
                <div className="settings-item-content">
                  <div className="settings-item-header">
                    <span className="settings-item-title">Enable Auto-Save</span>
                    <span className="settings-item-description">Automatically save your work as you type</span>
                  </div>
                  <label className="modern-toggle">
                    <input
                      type="checkbox"
                      checked={localSettings.autoSave}
                      onChange={(e) => handleChange('autoSave', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>

              {localSettings.autoSave && (
                <div className="settings-item">
                  <div className="settings-item-content">
                    <div className="settings-item-header">
                      <span className="settings-item-title">Auto-Save Interval</span>
                      <span className="settings-item-description">How often to save automatically</span>
                    </div>
                    <select
                      className="modern-select"
                      value={localSettings.autoSaveInterval || 30000}
                      onChange={(e) => handleChange('autoSaveInterval', parseInt(e.target.value))}
                    >
                      <option value={15000}>15 seconds</option>
                      <option value={30000}>30 seconds</option>
                      <option value={60000}>1 minute</option>
                      <option value={300000}>5 minutes</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div className="settings-group">
              <h3 className="settings-group-title">Display Options</h3>
              <div className="settings-item">
                <div className="settings-item-content">
                  <div className="settings-item-header">
                    <span className="settings-item-title">Show Verse Numbers</span>
                    <span className="settings-item-description">Display verse numbers in the interface</span>
                  </div>
                  <label className="modern-toggle">
                    <input
                      type="checkbox"
                      checked={localSettings.showVerseNumbers}
                      onChange={(e) => handleChange('showVerseNumbers', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div className="settings-item">
                <div className="settings-item-content">
                  <div className="settings-item-header">
                    <span className="settings-item-title">Highlight Keywords</span>
                    <span className="settings-item-description">Highlight important keywords in verses</span>
                  </div>
                  <label className="modern-toggle">
                    <input
                      type="checkbox"
                      checked={localSettings.highlightKeywords}
                      onChange={(e) => handleChange('highlightKeywords', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      case 'appearance':
        return (
          <div className="settings-content">
            <div className="settings-group">
              <h3 className="settings-group-title">Theme</h3>
              <div className="theme-grid">
                {themes.map(theme => (
                  <div
                    key={theme.id}
                    className={`theme-card ${localSettings.theme === theme.id ? 'selected' : ''}`}
                    onClick={() => handleChange('theme', theme.id)}
                  >
                    <div className="theme-preview" style={{ background: theme.gradient }}>
                      <div className="theme-sample-card"></div>
                      <div className="theme-sample-text"></div>
                    </div>
                    <div className="theme-info">
                      <span className="theme-name">{theme.name}</span>
                      <div className="theme-color-dot" style={{ backgroundColor: theme.color }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="settings-group">
              <h3 className="settings-group-title">Typography</h3>
              <div className="settings-item">
                <div className="settings-item-content">
                  <div className="settings-item-header">
                    <span className="settings-item-title">Font Size</span>
                    <span className="settings-item-description">Choose your preferred text size</span>
                  </div>
                  <select
                    className="modern-select"
                    value={localSettings.fontSize}
                    onChange={(e) => handleChange('fontSize', e.target.value)}
                  >
                    {fontSizes.map(size => (
                      <option key={size.id} value={size.id}>{size.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        );

      case 'bible':
        return (
          <div className="settings-content">
            <div className="settings-group">
              <h3 className="settings-group-title">Bible Translation</h3>
              <div className="settings-item">
                <div className="settings-item-content">
                  <div className="settings-item-header">
                    <span className="settings-item-title">Default Translation</span>
                    <span className="settings-item-description">Choose your preferred Bible translation</span>
                  </div>
                  <select
                    className="modern-select"
                    value={localSettings.defaultTranslation}
                    onChange={(e) => handleChange('defaultTranslation', e.target.value)}
                  >
                    {BIBLE_TRANSLATIONS.map(translation => (
                      <option key={translation.code} value={translation.code}>
                        {translation.name} ({translation.language})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        );

      case 'export':
        return (
          <div className="settings-content">
            <div className="settings-group">
              <h3 className="settings-group-title">Export Preferences</h3>
              <div className="settings-item">
                <div className="settings-item-content">
                  <div className="settings-item-header">
                    <span className="settings-item-title">Default Format</span>
                    <span className="settings-item-description">Choose your preferred export format</span>
                  </div>
                  <select
                    className="modern-select"
                    value={localSettings.exportFormat || 'pdf'}
                    onChange={(e) => handleChange('exportFormat', e.target.value)}
                  >
                    <option value="pdf">📄 PDF Document</option>
                    <option value="word">📝 Word Document</option>
                    <option value="html">🌐 HTML Web Page</option>
                  </select>
                </div>
              </div>

              <div className="settings-item">
                <div className="settings-item-content">
                  <div className="settings-item-header">
                    <span className="settings-item-title">Include Verse Numbers</span>
                    <span className="settings-item-description">Show verse numbers in exported documents</span>
                  </div>
                  <label className="modern-toggle">
                    <input
                      type="checkbox"
                      checked={localSettings.includeVerseNumbers || false}
                      onChange={(e) => handleChange('includeVerseNumbers', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="settings-modal-overlay">
      <div className="settings-modal">
        <div className="settings-modal-header">
          <h2 className="settings-modal-title">Settings</h2>
          <button className="settings-close-btn" onClick={onClose}>
            <span className="close-icon">×</span>
          </button>
        </div>

        <div className="settings-modal-body">
          <div className="settings-sidebar">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="settings-main">
            {renderTabContent()}
          </div>
        </div>

        <div className="settings-modal-footer">
          <button className="btn-modern-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-modern-primary" onClick={handleSave}>
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
}

export default Settings;